import api from "../services/api"
import Skeleton from "./skeleton/Skeleton"
import { Helmet } from "react-helmet"
import AvailableUnitsTracker from "../components/AvailableUnitsTracker"
import { Trash, EditIcon } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import Swal from "sweetalert2"

const emptyForm = {
  unitNumber: "",
  tenant: "",
  name: "",
  monthlyAmortization: "",
}

export default function Units() {
  // -----------------------------
  // State
  // -----------------------------
  const [units, setUnits] = useState([])
  const [rooms, setRooms] = useState([])
  const [tenants, setTenants] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [errors, setErrors] = useState({})
  const [isServerOnline, setIsServerOnline] = useState(true)
  const [errorMsg, setErrorMsg] = useState("")
  const [monthlyAmmortization, setMonthlyAmmortization] = useState("")

  const [filters, setFilters] = useState({
    status: "All",
    floor: "",
    search: "",
  })

  const [formData, setFormData] = useState(emptyForm)

  const [selectedUnit, setSelectedUnit] = useState(null)
  const [unitToDelete, setUnitToDelete] = useState(null)

  const [isModalOpen, setIsModalOpen] = useState(false) // edit
  const [isAddModalOpen, setIsAddModalOpen] = useState(false) // add/assign
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const [loadingAddTenant, setLoadingAddTenant] = useState(false)

  // -----------------------------
  // Helpers
  // -----------------------------
  const validateForm = () => {
    const newErrors = {}

    if (!formData.unitNumber.trim())
      newErrors.unitNumber = "Unit number is required"
    if (!formData.tenant) newErrors.tenant = "Tenant selection is required"

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target

    setFormData((prev) => {
      const newFormData = { ...prev, [name]: value }

      if (name === "tenant") {
        newFormData.name =
          tenants.find((tenant) => tenant._id === value)?.name || ""
      }

      return newFormData
    })

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }))
    }
  }

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  // -----------------------------
  // Data Fetching
  // -----------------------------
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const res = await api.get(`/api/units`)
        setUnits(res.data)
      } catch {
        setError("Failed to load units")
      } finally {
        setLoading(false)
      }
    }

    fetchUnits()
  }, [])

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const res = await api.get(`/api/rooms`)

        setRooms(res.data)
      } catch (err) {
        console.error("Failed to load rooms", err)
      }
    }

    fetchRooms()
  }, [])

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const res = await api.get(`/api/tenants`)
        setTenants(res.data)
        console.log(res.data)
        setIsServerOnline(true)
      } catch (err) {
        console.error("Failed to load tenants", err)
        setIsServerOnline(false)
        setTenants([])
        setErrorMsg("The server is currently offline. Please refresh the page.")
      }
    }

    fetchTenants()
  }, [])

  // -----------------------------
  // Derived Data (Memoized)
  // -----------------------------
  const assignedRoomNumbers = useMemo(() => {
    return new Set(units.map((u) => u.unitNumber).filter(Boolean))
  }, [units])

  const availableRooms = useMemo(() => {
    return rooms.filter((room) => !assignedRoomNumbers.has(room.roomNumber))
  }, [rooms, assignedRoomNumbers])

  const availableRoomsForEdit = useMemo(() => {
    if (!selectedUnit) return availableRooms
    return rooms.filter(
      (room) =>
        room.roomNumber === selectedUnit.unitNumber ||
        !assignedRoomNumbers.has(room.roomNumber)
    )
  }, [rooms, availableRooms, assignedRoomNumbers, selectedUnit])

  const assignedTenantIds = useMemo(() => {
    return new Set(
      units
        .map((u) => (u.tenant?._id ? u.tenant._id : u.tenant))
        .filter(Boolean)
    )
  }, [units])

  const availableTenants = useMemo(() => {
    return tenants.filter((t) => !assignedTenantIds.has(t._id))
  }, [tenants, assignedTenantIds])

  const availableTenantsForEdit = useMemo(() => {
    if (!selectedUnit) return availableTenants

    const currentTenantId = selectedUnit.tenant?._id
      ? selectedUnit.tenant._id
      : selectedUnit.tenant

    return tenants.filter(
      (t) => t._id === currentTenantId || !assignedTenantIds.has(t._id)
    )
  }, [tenants, assignedTenantIds, selectedUnit, availableTenants])

  const filteredUnits = useMemo(() => {
    return units.filter((unit) => {
      const matchesStatus =
        filters.status === "All" || unit.status === filters.status
      const matchesFloor =
        !filters.floor || String(unit.floor) === filters.floor

      const q = filters.search?.toLowerCase()
      const matchesSearch =
        !q ||
        unit.unitNumber?.toLowerCase().includes(q) ||
        unit.type?.toLowerCase().includes(q)

      return matchesStatus && matchesFloor && matchesSearch
    })
  }, [units, filters])

  // -----------------------------
  // Actions (CRUD)
  // -----------------------------
  const openEditModal = (unit) => {
    setSelectedUnit(unit)
    setErrors({})

    setFormData({
      unitNumber: unit.unitNumber || "",
      tenant: unit.tenant?._id || unit.tenant || "",
      name: unit.name || "",
      monthlyAmortization: unit.monthlyAmortization || "",
    })

    setIsModalOpen(true)
  }

  const handleUpdate = async (e) => {
    e.preventDefault()
    if (!validateForm()) return

    try {
      const payload = {
        unitNumber: formData.unitNumber,
        tenant: formData.tenant || null,
        name: formData?.name || null,
        monthlyAmortization: String(formData.monthlyAmortization) || null,
      }

      const res = await api.put(`/api/units/${selectedUnit._id}`, payload)

      setIsModalOpen(false)

      await Swal.fire({
        icon: "success",
        title: "Updated!",
        text: "Assigning unit has been saved.",
        confirmButtonColor: "#f97316",
        timer: 1500,
        showConfirmButton: false,
      })

      setUnits((prev) =>
        prev.map((u) => (u._id === selectedUnit._id ? res.data : u))
      )

      setSelectedUnit(null)
      setErrors({})
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update unit")
    }
  }

  const handleAddUnit = async (e) => {
    e.preventDefault()
    setLoadingAddTenant(true)

    const isValid = validateForm()
    if (!isValid) {
      setLoadingAddTenant(false)
      return
    }

    try {
      const payload = {
        unitNumber: formData.unitNumber,
        tenant: formData.tenant || null,
        name: formData?.name || null,
        monthlyAmortization: monthlyAmmortization || null,
      }

      const res = await api.post(`/api/units`, payload)

      Swal.fire({
        icon: "success",
        text: "Add successfully.",
        confirmButtonColor: "#f97316",
        timer: 1200,
        showConfirmButton: false,
      })

      setUnits((prev) => [...prev, res.data])
      setIsAddModalOpen(false)
      setFormData(emptyForm)
      setErrors({})
    } catch (err) {
      if (api.isAxiosError(err) && !err.response) {
        Swal.fire({
          icon: "error",
          title: "Backend is offline",
          text: "Cannot connect to server. Start the server and try again.",
        })
        return
      }

      Swal.fire({
        icon: "error",
        title: "Update failed",
        text:
          err.response?.data?.message ||
          "Something went wrong while saving changes.",
      })
    } finally {
      setLoadingAddTenant(false)
    }
  }

  const confirmDelete = async () => {
    if (!unitToDelete) return

    try {
      await api.delete(`/api/units/${unitToDelete._id}`)
      setUnits((prev) => prev.filter((u) => u._id !== unitToDelete._id))

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        confirmButtonColor: "#d1001f",
        timer: 1500,
        showConfirmButton: false,
      })
    } catch {
      alert("Failed to delete unit")
    } finally {
      setIsDeleteModalOpen(false)
      setUnitToDelete(null)
    }
  }

  // -----------------------------
  // UI
  // -----------------------------
  return (
    <>
      <Helmet>
        <title>Units - Stay Suite | Apartment Management System</title>
      </Helmet>
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <Skeleton />
        ) : (
          <>
            <h1 className="text-2xl font-bold mb-2">Floor Units</h1>
            <p className="pb-5 text-gray-600 italic text-balance text-sm">
              Use this module to register floor units, track availability, and
              assign tenants accordingly.
            </p>
            <AvailableUnitsTracker units={units} rooms={rooms} />
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <input
                  type="text"
                  name="search"
                  placeholder="Search unit or type..."
                  value={filters.search}
                  onChange={handleFilterChange}
                  className="bg-white h-10 rounded-lg border border-gray-300 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                />

                <input
                  type="number"
                  name="floor"
                  placeholder="Floor"
                  value={filters.floor}
                  onChange={handleFilterChange}
                  className="bg-white h-10 rounded-lg border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 w-full md:w-24"
                />

                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="bg-white h-10 rounded-lg border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                >
                  <option value="All">All Status</option>
                  <option value="Vacant">Vacant</option>
                  <option value="Occupied">Occupied</option>
                </select>
              </div>

              <button
                onClick={() => {
                  setFormData(emptyForm)
                  setErrors({})
                  setIsAddModalOpen(true)
                }}
                className="h-10 w-full sm:w-auto rounded-lg bg-orange-500 px-5 text-sm font-semibold text-white hover:bg-orange-600 shadow-sm"
              >
                + Assign Unit
              </button>
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="overflow-x-auto">
                <table className="min-w-160 w-full border-collapse">
                  <thead className="sticky top-0 bg-gray-50 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Room Number
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Tenant
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Monthly Payment
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 overflow-hidden ellipsis text-nowrap">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
                    {loading && (
                      <tr>
                        <td
                          colSpan="5"
                          className="py-6 text-center text-gray-500"
                        >
                          Loading...
                        </td>
                      </tr>
                    )}

                    {!loading && error && (
                      <tr>
                        <td
                          colSpan="5"
                          className="py-6 text-center text-red-700"
                        >
                          {error}
                        </td>
                      </tr>
                    )}

                    {!loading && !error && filteredUnits.length === 0 && (
                      <tr>
                        <td
                          colSpan="6"
                          className="py-6 text-center text-gray-400"
                        >
                          No matching units found
                        </td>
                      </tr>
                    )}

                    {!loading &&
                      !error &&
                      filteredUnits.map((unit, index) => (
                        <tr
                          key={unit._id}
                          className={`${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                        >
                          <td className="px-4 py-3 font-medium text-gray-900">
                            {unit.unitNumber}
                          </td>

                          <td className="px-4 py-3 font-medium text-gray-900">
                            {unit.name}
                          </td>

                          <td className="px-4 py-3 font-medium text-gray-900">
                            {unit.monthlyAmortization}
                          </td>
                          <td className="px-4 py-3 flex gap-2">
                            <button
                              onClick={() => openEditModal(unit)}
                              className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
                            >
                              <EditIcon size={18} />
                            </button>

                            <button
                              onClick={() => {
                                setUnitToDelete(unit)
                                setIsDeleteModalOpen(true)
                              }}
                              className="p-2 rounded-lg  text-red-500 hover:bg-red-200 transition"
                            >
                              <Trash size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
            {!isServerOnline && (
              <div className="mt-4 mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {errorMsg}
              </div>
            )}
          </>
        )}
      </div>

      {/* EDIT MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-800 mb-1">
              Edit Unit
            </h2>
            <p className="text-sm text-gray-500 mb-5">Update unit details</p>

            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Unit Number (Room)
                </label>

                <select
                  name="unitNumber"
                  value={formData.unitNumber}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Select room</option>
                  {availableRoomsForEdit.map((room) => (
                    <option key={room._id} value={room.roomNumber}>
                      {room.roomNumber}
                    </option>
                  ))}
                </select>

                {errors.unitNumber && (
                  <p className="text-xs text-red-500">{errors.unitNumber}</p>
                )}

                <label className="text-sm font-medium text-gray-700 mt-3 block">
                  Tenant
                </label>

                <select
                  name="tenant"
                  value={formData.tenant}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Select tenant</option>
                  {availableTenantsForEdit.length > 0 ? (
                    availableTenantsForEdit.map((tenant) => (
                      <option key={tenant._id} value={tenant._id}>
                        {tenant.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>No available tenants</option>
                  )}
                </select>

                {errors.tenant && (
                  <p className="text-xs text-red-500">{errors.tenant}</p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-lg border text-sm text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-orange-600 text-white hover:bg-orange-700 transition"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-sm rounded-xl shadow-2xl p-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Delete Unit
            </h2>

            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete unit{" "}
              <span className="font-semibold">{unitToDelete?.unitNumber}</span>?
              This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false)
                  setUnitToDelete(null)
                }}
                className="px-4 py-2 rounded-lg border text-sm text-gray-700 hover:bg-gray-100 transition"
              >
                Cancel
              </button>

              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD / ASSIGN MODAL */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-800 mb-1">
              Assign Unit
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              Place a tenant in a unit
            </p>

            <form onSubmit={handleAddUnit} className="space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Room
                </label>

                <select
                  name="unitNumber"
                  value={formData.unitNumber}
                  onChange={(e) => {
                    handleChange(e) // Updates formData with the selected room
                    const selectedRoom = availableRooms.find(
                      (room) => room.roomNumber === e.target.value
                    )

                    // Ensure that monthlyAmmortization is set correctly
                    setMonthlyAmmortization(
                      selectedRoom ? selectedRoom.monthlyAmortization : ""
                    )
                  }}
                  className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                    errors.unitNumber ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">Available Unit</option>
                  {availableRooms.map((room) => (
                    <option key={room._id} value={room.roomNumber}>
                      {room.roomNumber}
                    </option>
                  ))}
                </select>

                {errors.unitNumber && (
                  <p className="text-xs text-red-500">{errors.unitNumber}</p>
                )}
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Monthly Ammortization
                </label>

                <input
                  type="number"
                  name="monthlyAmortization"
                  className="w-full rounded-lg border border-orange-500 px-3 py-2 text-sm focus:outline-none focus:ring-2 pointer-events-none"
                  value={monthlyAmmortization || ""}
                  placeholder="Please provide ammortization"
                  disabled
                />

                {errors.unitNumber && (
                  <p className="text-xs text-red-500">{errors.unitNumber}</p>
                )}
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Tenant
                </label>

                <select
                  name="tenant"
                  value={formData.tenant}
                  onChange={handleChange}
                  className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                    errors.tenant ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">Please select tenant</option>
                  {availableTenants.length > 0 ? (
                    availableTenants.map((tenant) => (
                      <option key={tenant._id} value={tenant._id}>
                        {tenant.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>No available tenants</option>
                  )}
                </select>

                {errors.tenant && (
                  <p className="text-xs text-red-500">{errors.tenant}</p>
                )}

                <input type="hidden" name="tenant" value={formData.name} />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 rounded-lg border text-sm text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={loadingAddTenant}
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {loadingAddTenant ? "Saving..." : "Assign Tenant"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
